import React from "react";
import { useI18n } from "../lib/i18n.jsx";
import { Scroll } from "lucide-react";

const CONTROLLER = {
  name: "TIOUW.com B.V.",
  address: "Binderij 7-L, 1185 ZH Amstelveen, Nederland",
  kvk: "05076640",
  email: "joost@tiouw.com",
};

export default function TermsPage() {
  const { lang } = useI18n();
  const isNL = lang === "nl";
  const lastUpdated = "2026-07-06";

  return (
    <main data-testid="terms-page" className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <p className="label-small text-[#8B3A3A] mb-4 flex items-center gap-2">
        <Scroll size={12} /> {isNL ? "Gebruiksvoorwaarden" : "Terms of Use"}
      </p>
      <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918] mb-4">
        {isNL ? "Gebruiksvoorwaarden." : "Terms of Use."}
      </h1>
      <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#7A7061] mb-10">
        {isNL ? `Laatst bijgewerkt: ${lastUpdated}` : `Last updated: ${lastUpdated}`}
      </p>

      <article className="text-[#4A4238] leading-relaxed text-[15px] space-y-6">
        {isNL ? <Dutch c={CONTROLLER} /> : <English c={CONTROLLER} />}
      </article>
    </main>
  );
}

function H2({ children }) {
  return <h2 className="font-display text-2xl mt-8 mb-3 text-[#1A1918]">{children}</h2>;
}

function Dutch({ c }) {
  return (
    <>
      <p>
        Welkom bij <strong>NLP Magick</strong>. Door een account aan te maken of de
        applicatie te gebruiken ga je akkoord met deze voorwaarden.
      </p>

      <H2>1. Aanbieder</H2>
      <p>
        De applicatie wordt aangeboden door <strong>{c.name}</strong>, {c.address}, KvK {c.kvk},
        bereikbaar via <a className="underline underline-offset-2" href={`mailto:${c.email}`}>{c.email}</a>.
      </p>

      <H2>2. Gebruik van je account</H2>
      <ul className="list-disc list-inside space-y-1">
        <li>Je bent zelf verantwoordelijk voor het geheim houden van je wachtwoord en, indien ingeschakeld, je 2FA-codes.</li>
        <li>Je gebruikt de applicatie uitsluitend voor je eigen magische studie en dagboek.</li>
        <li>Gedeelde inhoud in het Pantheon dient respectvol en niet-schadelijk te zijn. De Adept kan inhoud verwijderen die deze voorwaarden schendt.</li>
      </ul>

      <H2>3. Inhoud, graden en rituelen</H2>
      <p>
        Rituelen, lezingen en scrolls in de Bibliotheek zijn bedoeld voor studiedoeleinden.
        Het volgen ervan is geheel op eigen initiatief en verantwoordelijkheid. De aanbieder
        geeft geen therapeutische, medische of juridische adviezen.
      </p>

      <H2>4. Live-evenementen en de NLP Magick opleiding</H2>
      <p>
        Aan de live-evenementen en de NLP Magick opleiding gelden aanvullende voorwaarden
        en, in het geval van de Magiëropleiding, een vooropleiding (ABC-NLP). Deze
        applicatie is geen aanmeldingssysteem — aanmelding gebeurt via
        <a className="underline underline-offset-2 mx-1" href="https://www.tiouw.com" target="_blank" rel="noopener noreferrer">www.tiouw.com</a>.
      </p>

      <H2>5. Beperking van aansprakelijkheid</H2>
      <p>
        De aanbieder spant zich in de dienst zonder onderbrekingen te leveren maar geeft
        geen garantie op ononderbroken beschikbaarheid. De aanbieder is niet aansprakelijk
        voor indirecte schade voortvloeiend uit het gebruik van de applicatie.
      </p>

      <H2>6. Intellectueel eigendom</H2>
      <p>
        Alle door de Adept aangeleverde teksten en materialen blijven eigendom van de
        aanbieder of hun oorspronkelijke rechthebbenden. Persoonlijke inhoud (dagboek,
        pantheon-inzendingen) blijft eigendom van jouzelf.
      </p>

      <H2>7. Wijzigingen</H2>
      <p>
        Wij mogen deze voorwaarden aanpassen. Belangrijke wijzigingen worden in de
        applicatie aangekondigd.
      </p>

      <H2>8. Toepasselijk recht</H2>
      <p>
        Nederlands recht is van toepassing. Geschillen worden voorgelegd aan de bevoegde
        rechter in het arrondissement waar de aanbieder is gevestigd.
      </p>
    </>
  );
}

function English({ c }) {
  return (
    <>
      <p>
        Welcome to <strong>NLP Magick</strong>. By creating an account or using this application
        you accept these terms.
      </p>

      <H2>1. Provider</H2>
      <p>
        The application is operated by <strong>{c.name}</strong>, {c.address}, Chamber of
        Commerce {c.kvk}, reachable at <a className="underline underline-offset-2" href={`mailto:${c.email}`}>{c.email}</a>.
      </p>

      <H2>2. Your account</H2>
      <ul className="list-disc list-inside space-y-1">
        <li>You are responsible for keeping your password (and, if enabled, your 2FA codes) private.</li>
        <li>You use the application only for your own magickal study and journal.</li>
        <li>Shared content in the Pantheon must be respectful and non-harmful. The Adept may remove content that violates these terms.</li>
      </ul>

      <H2>3. Content, grades and rituals</H2>
      <p>
        Rituals, lectures and scrolls in the Library are provided for study. Practising them
        is entirely at your own initiative and responsibility. The provider does not
        supply therapeutic, medical or legal advice.
      </p>

      <H2>4. Live events and the NLP Magick course</H2>
      <p>
        Additional terms apply to the live events and the NLP Magick course, and the
        Magician Course requires the prerequisite ABC-NLP training. This application is
        not a sign-up system — registration for those events runs through
        <a className="underline underline-offset-2 mx-1" href="https://www.tiouw.com" target="_blank" rel="noopener noreferrer">www.tiouw.com</a>.
      </p>

      <H2>5. Limitation of liability</H2>
      <p>
        The provider aims to deliver the service without interruptions but offers no
        guarantee of uninterrupted availability. The provider is not liable for indirect
        damages arising from use of the application.
      </p>

      <H2>6. Intellectual property</H2>
      <p>
        All texts and materials contributed by the Adept remain the property of the
        provider or their original rights-holders. Personal content (diary, pantheon
        entries) remains your own.
      </p>

      <H2>7. Changes</H2>
      <p>We may amend these terms. Material changes will be announced in the application.</p>

      <H2>8. Governing law</H2>
      <p>
        Dutch law applies. Any dispute will be brought before the competent court in the
        provider’s registered district.
      </p>
    </>
  );
}
